import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom, timeout } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../models/api.model';
import {
	BrandOffice,
	BrandView,
	OperatingHours,
	TenantProfile,
} from '../models/tenant-profile.model';
import { TenantService } from './tenant.service';

const DAY_LABELS: { key: string; label: string }[] = [
	{ key: 'mon', label: 'Sen' },
	{ key: 'tue', label: 'Sel' },
	{ key: 'wed', label: 'Rab' },
	{ key: 'thu', label: 'Kam' },
	{ key: 'fri', label: 'Jum' },
	{ key: 'sat', label: 'Sab' },
	{ key: 'sun', label: 'Min' },
];

/** Format operatingHours jsonb → ringkas, mis. "Sen–Jum 08.00–17.00, Sab 09.00–14.00". */
function formatOperatingHours(oh: OperatingHours | null | undefined): string {
	if (!oh) return '';
	const segs: string[] = [];
	let i = 0;
	while (i < DAY_LABELS.length) {
		const cur = oh[DAY_LABELS[i].key];
		if (!cur) {
			i += 1;
			continue;
		}
		// Kumpulkan hari berturut-turut dengan jam identik.
		let j = i;
		while (
			j + 1 < DAY_LABELS.length &&
			oh[DAY_LABELS[j + 1].key]?.open === cur.open &&
			oh[DAY_LABELS[j + 1].key]?.close === cur.close
		) {
			j += 1;
		}
		const range = `${cur.open}–${cur.close}`.replace(/:/g, '.');
		segs.push(
			i === j
				? `${DAY_LABELS[i].label} ${range}`
				: `${DAY_LABELS[i].label}–${DAY_LABELS[j].label} ${range}`,
		);
		i = j + 1;
	}
	return segs.join(', ');
}

/**
 * Identitas & kontak tenant (footer/header/contact/WA-float) — dinamis dari API.
 *
 * `load()` dipanggil di APP_INITIALIZER → fetch saat SSR; `provideClientHydration()`
 * meng-cache GET ke TransferState sehingga browser tak fetch ulang. Bila gagal,
 * `brand`/`offices` jatuh ke `environment.brand` (tetap tampil).
 */
@Injectable({ providedIn: 'root' })
export class TenantProfileService {
	private readonly http = inject(HttpClient);
	private readonly tenant = inject(TenantService);

	private readonly profile = signal<TenantProfile | null>(null);

	/** Identitas siap-pakai (override API → fallback environment.brand). */
	readonly brand = computed<BrandView>(() => {
		const p = this.profile();
		const fb = environment.brand;
		const s = p?.social ?? {};
		const env = fb as unknown as Record<string, string>;
		return {
			name: p?.name || fb.name,
			tagline: p?.tagline || fb.tagline,
			phone: p?.phone || fb.phone,
			whatsapp: p?.whatsapp || fb.whatsapp,
			email: p?.email || fb.email,
			address: p?.address || fb.address,
			logoUrl: p?.logoUrl || '',
			instagram: s['instagram'] || env['instagram'] || '',
			facebook: s['facebook'] || env['facebook'] || '',
			youtube: s['youtube'] || env['youtube'] || '',
			tiktok: s['tiktok'] || '',
			x: s['x'] || '',
		};
	});

	/** Warna tema (primary→brand, accent→gold). Fallback ke palet landing existing. */
	readonly theme = computed(() => {
		const p = this.profile();
		return {
			primary: p?.primaryColor || '#10b981', // ~ brand-500 existing
			accent: p?.accentColor || '#c98f2f', // ~ gold-500 existing
		};
	});

	/** Daftar kantor siap-pakai (jam diformat). Fallback environment / 1 kantor brand. */
	readonly offices = computed<BrandOffice[]>(() => {
		const p = this.profile();
		const fb = environment.brand;
		if (p?.offices?.length) {
			return p.offices.map((o) => ({
				name: o.name,
				type: o.type || (o.isHeadOffice ? 'pusat' : 'cabang'),
				address: o.address || '',
				phone: o.phone || undefined,
				whatsapp: o.whatsapp || undefined,
				email: o.email || undefined,
				hours: formatOperatingHours(o.operatingHours),
				mapsQuery: o.mapsQuery || undefined,
			}));
		}
		const envOffices = (fb as unknown as { offices?: BrandOffice[] }).offices;
		if (envOffices?.length) return envOffices;
		return [
			{
				name: 'Kantor Pusat',
				type: 'pusat',
				address: fb.address,
				phone: fb.phone,
				whatsapp: fb.whatsapp,
				email: fb.email,
			},
		];
	});

	/** Fetch profil tenant (dipanggil sekali saat bootstrap). */
	load(): Promise<void> {
		// timeout 5s: saat SSR/prerender (mis. build CI) API bisa lambat/tak terjangkau.
		// Tanpa batas ini, request menggantung → app tak "stabilize" dalam 9s & build warning.
		// Bila timeout/gagal → fallback ke environment.brand (lihat computed brand()).
		return firstValueFrom(
			this.http
				.get<ApiResponse<TenantProfile>>(`${environment.apiBaseUrl}/public/tenant-profile`, {
					params: this.tenant.params(),
				})
				.pipe(timeout(5000)),
		)
			.then((res) => this.profile.set(res.data ?? null))
			.catch(() => this.profile.set(null));
	}
}
